import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import type { ReactNode } from "react";

export type SlidePageTransitionDirection = "forward" | "back";

interface SlidePageTransitionProps {
  children: ReactNode;
  className?: string;
}

interface TransitionLocationState {
  transitionDirection?: SlidePageTransitionDirection;
}

const familyManagementFlowRank = (pathname: string) => {
  if (pathname === "/family/parents") return 0;
  if (pathname === "/family/parents/new") return 1;
  if (/^\/family\/parent\/[^/]+\/details$/.test(pathname)) return 1;
  if (/^\/family\/parent\/[^/]+\/edit$/.test(pathname)) return 2;

  if (pathname === "/family/children") return 0;
  if (pathname === "/family/children/new") return 1;
  if (/^\/family\/children\/[^/]+\/details$/.test(pathname)) return 1;
  if (/^\/family\/children\/[^/]+\/edit$/.test(pathname)) return 2;

  if (pathname === "/family/families") return 0;
  if (pathname === "/family/new") return 1;
  if (/^\/family\/[^/]+\/details$/.test(pathname)) return 1;
  if (/^\/family\/[^/]+\/edit$/.test(pathname)) return 2;

  return null;
};

let previousFamilyManagementFlowPath: string | null = null;

const resolveDirection = (
  pathname: string,
  state: TransitionLocationState | null | undefined
): SlidePageTransitionDirection => {
  if (state?.transitionDirection === "forward" || state?.transitionDirection === "back") {
    return state.transitionDirection;
  }

  const previousRank = previousFamilyManagementFlowPath ? familyManagementFlowRank(previousFamilyManagementFlowPath) : null;
  const currentRank = familyManagementFlowRank(pathname);
  if (previousRank !== null && currentRank !== null && currentRank < previousRank) {
    return "back";
  }

  return "forward";
};

const SlidePageTransition = ({ children, className = "" }: SlidePageTransitionProps) => {
  const location = useLocation();
  const direction = useMemo(
    () => resolveDirection(location.pathname, location.state as TransitionLocationState | null),
    [location.pathname, location.state]
  );

  React.useEffect(() => {
    previousFamilyManagementFlowPath = location.pathname;
  }, [location.pathname]);

  return (
    <div className={`lumen-slide-page lumen-slide-page--${direction} ${className}`.trim()}>
      {children}
    </div>
  );
};

export default SlidePageTransition;
